import { timeAgoWithoutPrefix } from "./dateUtils"
import React from "react"
import i18n_iso_countrie_de from "i18n-iso-countries/langs/de.json"
import i18n_iso_countrie_en from "i18n-iso-countries/langs/en.json"
import countries from "i18n-iso-countries"
import Cookies from "universal-cookie"

countries.registerLocale(i18n_iso_countrie_de)
countries.registerLocale(i18n_iso_countrie_en)

function applyStoredLocalstorageMap(raw) {
    if (typeof raw !== "string" || raw === "") {
        return
    }
    let jsonArr = {}
    try {
        const parsed = JSON.parse(raw)
        if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
            jsonArr = parsed
        }
    } catch {
        jsonArr = {}
    }
    for (const [key, value] of Object.entries(jsonArr)) {
        if (key === "login" || key === "presetId") {
            continue
        }
        if (typeof value !== "string") {
            continue
        }
        localStorage.setItem(key, value)
    }
}

function applySettingDefaults(settings) {
    if (!Array.isArray(settings)) {
        return
    }
    settings.forEach(function (value) {
        if (!Array.isArray(value)) {
            return
        }
        value.forEach(function (value2) {
            if (value2 == null || typeof value2.name !== "string" || value2.name === "") {
                return
            }
            if (!localStorage.getItem(value2.name)) {
                localStorage.setItem(value2.name, value2.defaultValue)
            }
        })
    })
}

class PPUtils {
    static isDev() {
        if (window.location.hostname == "localhost" && window.location.port == "3000") {
            return true
        }
        return false
    }

    static PRICE_MANAGEMENT_ENABLED_HOSTS = new Set([
        "pp-demo.sylphen.com",
        "mspreispiranha.mstore.musicstore.de",
        "pp-werkzeugstore24.sylphen.com",
    ])

    static isPpDemo() {
        return window.location.hostname === "pp-demo.sylphen.com"
    }

    static isPriceManagementEnabledHost() {
        if (PPUtils.isDev()) {
            return true
        }
        return PPUtils.PRICE_MANAGEMENT_ENABLED_HOSTS.has(window.location.hostname)
    }

    static hasPriceManagementAccess(ruleEngineAccess) {
        return !!ruleEngineAccess || PPUtils.isPriceManagementEnabledHost()
    }

    static isTariffComparisonMode(modus) {
        if (modus === "Y") {
            return true
        }
        return false
    }

    static get Colors() {
        return {
            GreenStart: "#56b455",
            GreenEnd: "#30b314",
            YellowStart: "#f8de52",
            YellowEnd: "#ffc939",
            RedStart: "#ff7451",
            RedEnd: "#ff3636",
            NeutralStart: "#007bff",
            NeutralEnd: "#005cbd",
            OrangeStart: "#ff963a",
            OrangeEnd: "#ff5529",
            GreyLight: "#bbbbbb",
            Black: "#000000"
        }
    }

    static getColor(value, opacity = "0.2") {
        const hue = (((value + 1.0) / 2) * 120).toString(10)
        return ["hsla(", hue, ",100%,50%," + opacity + ")"].join("")
    }

    static getPriceMood(pricediff) {
        let colorhex = PPUtils.Colors.YellowStart
        if (localStorage.getItem("showPriceDifferenceThumb") === "false") {
            colorhex = PPUtils.Colors.Black
        }
        let arrow = ""
        if (pricediff > 0) {
            colorhex = PPUtils.Colors.GreenStart
            arrow = "fas fa-thumbs-up"
        } else if (pricediff < 0) {
            colorhex = PPUtils.Colors.RedStart
            arrow = "fas fa-thumbs-down"
        }
        return { color: colorhex, arrow: arrow }
    }

    static getCrawlAgo(crawlDate, i18n) {
        let currentAuth = PPUtils.getLogin()

        if(currentAuth?.customer?.name === 'Verivox') {
            return crawlDate
        }

        return timeAgoWithoutPrefix(crawlDate, i18n.language)
    }

    static getPercentDifference(price1, price2) {
        let price1f = parseFloat(price1)
        let price2f = parseFloat(price2)

        let percentage = (price2f - price1f) / Math.abs(price1f)
        let number = Math.min(1, Math.max(-1, percentage * 2))
        return Math.round(number * 1e2) / 1e2
    }

    static parseStoredLogin(raw) {
        if (raw == null || raw === "" || raw === "undefined" || raw === "null") {
            return null
        }
        let parsed
        try {
            parsed = JSON.parse(raw)
        } catch {
            return null
        }
        if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return null
        }
        if (typeof parsed.token !== "string" || parsed.token === "") {
            return null
        }
        return parsed
    }

    static setSettingsLocalstorage(settings) {
        try {
            applyStoredLocalstorageMap(PPUtils.getLogin()?.user?.configSettingsLocalstorage)
            applySettingDefaults(settings)
        } catch {
            // localStorage nicht verfügbar: Die Einstellungen laufen dann mit ihren Standardwerten.
        }
    }

    static decodeJwtPayload(token) {
        if (!token) return null
        try {
            const base64Url = token.split(".")[1]
            if (!base64Url) return null
            let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
            while (base64.length % 4) {
                base64 += "="
            }
            return JSON.parse(window.atob(base64))
        } catch {
            return null
        }
    }

    static isTokenExpired(loginData) {
        if (loginData == null || typeof loginData !== "object") {
            return true
        }
        if (typeof loginData.token !== "string" || loginData.token === "") {
            return true
        }
        const jwtParts = loginData.token.split(".")
        if (jwtParts.length !== 3) {
            return false
        }
        const payload = PPUtils.decodeJwtPayload(loginData.token)
        if (payload == null) {
            return true
        }
        if (payload.exp == null) {
            return false
        }
        if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
            return true
        }
        return Date.now() > payload.exp * 1000
    }

    static getLogin() {
        try {
            const localRaw = localStorage.getItem("login")
            const localLogin = PPUtils.parseStoredLogin(localRaw)
            if (localLogin) {
                if (PPUtils.isTokenExpired(localLogin)) {
                    localStorage.removeItem("login")
                } else {
                    return localLogin
                }
            } else if (localRaw) {
                localStorage.removeItem("login")
            }
            const sessionRaw = sessionStorage.getItem("login")
            const sessionLogin = PPUtils.parseStoredLogin(sessionRaw)
            if (sessionLogin) {
                if (PPUtils.isTokenExpired(sessionLogin)) {
                    sessionStorage.removeItem("login")
                } else {
                    return sessionLogin
                }
            } else if (sessionRaw) {
                sessionStorage.removeItem("login")
            }
        } catch {
            // Speicher nicht lesbar: wie "nicht eingeloggt" behandeln.
        }
        return null
    }

    static updateLogin(auth) {
        if (auth == null || typeof auth !== "object" || Array.isArray(auth)) {
            return
        }
        if (typeof auth.token !== "string" || auth.token === "") {
            return
        }
        try {
            const payload = JSON.stringify(auth)
            if (localStorage.getItem("login")) {
                localStorage.setItem("login", payload)
                return
            }
            sessionStorage.setItem("login", payload)
        } catch {
            // Speicher nicht verfügbar: Der aktualisierte Login wird dann nur nicht gespeichert.
        }
    }

    static removeLogin() {
        try {
            sessionStorage.removeItem("login")
            localStorage.removeItem("login")
        } catch {
            // blocked in iframe
        }
    }

    static addZeroesIfNeeded(num) {
        return num.toLocaleString("en", { useGrouping: false, minimumFractionDigits: 2 })
    }

    static getPriceDifference(price, secondPrice) {
        //return ((price - secondPrice) / price) * 100
        return ((price - secondPrice) * 100.0) / price
    }

    static getTariffComparisonModeProducerString(t) {
        let auth = PPUtils.getLogin()
        if(auth?.customer?.name === "Verivox Energy" || auth?.token_name === "Verivox Energy") {
            return t("overwrite.tariffcomparisonmode.energy.manufacturer")
        }

        return t("overwrite.tariffcomparisonmode.single.manufacturer")
    }

    static getMappedColumnName(label, t, i18n, tariffComparisonMode) {
        const columnNamesMapping = {
            avgPrice: "AVG " + t("single.price"),
            minPrice: "MIN " + t("single.price"),
            maxPrice: "MAX " + t("single.price"),
            productnr: t("single.productnr"),
            extartnr: t("single.external_item_number"),
            checked: "Checked",
            producer: PPUtils.isTariffComparisonMode(tariffComparisonMode)
                ? PPUtils.getTariffComparisonModeProducerString(t)
                : t("single.manufacturer"),
            department: t("single.department"),
            productCat: t("single.product_category"),
            productGroup: t("single.product_group"),
            productName: t("single.productname"),
            ean: "EAN",
            //Rule Engine Preisvorschlag Spalten - erstmal vom Namen hartcodiert - Bei anderen Kunden mit mehreren Channels müsste man sich hier nochmal was überlegen
            PMS_Price_Onlineshop: "Preisvorschlag",
            PMS_MargeProzent_Onlineshop: "Preisvorschlag Marge",
            ranking: t("single.ranking"),
            stockCount: t("single.stock_count"),
            appointed: "Appointed",
            discontinuedItem: t("single.discontinued_item"),
            packageunit: t("single.packageunit"),
            priceEk: "EK " + t("single.price"),
            imageUrl: t("single.image")
        }

        if (!label?.toLowerCase().includes("pms_") && label?.toLowerCase().includes("price_")) {
            return (
                <>
                    <span>{t("single.price")}</span>
                    <span
                        title={countries.getName(label.split("_")[1], i18n.language)}
                        className={"ms-1 align-center fi fi-" + label.split("_")[1]}
                    />
                </>
            )
        }
        return columnNamesMapping[label] !== undefined ? columnNamesMapping[label] : label
    }

    static isBooleanColumn(column) {
        const booleanColumns = ["checked", "appointed", "discontinuedItem"]
        return booleanColumns.includes(column)
    }

    static isFixedColumn(column) {
        const fixedColumns = ["imageUrl", "productName"]
        return fixedColumns.includes(column)
    }

    static getBooleanValue(val, t) {
        return val.toString() === "false" ? t("pricetable.boolean.false") : t("pricetable.boolean.true")
    }

    static getCustomerProductURL(customer, customerProduct, priceLang='default') {
        let singleLanguageCustomer = false;
        const productUrlVar = customer?.productUrl;
        if (PPUtils.isJSON(productUrlVar)) {
            try {
                const jsonProductUrl = JSON.parse(productUrlVar)
                const url =  jsonProductUrl[priceLang] + customerProduct.productnr
                return url;
            } catch (error) {
                singleLanguageCustomer = true;
            }            
        } else {
            singleLanguageCustomer = true;
        }
        if (singleLanguageCustomer) {
            if (
                customerProduct.urls !== undefined &&
                customerProduct.urls.de !== undefined &&
                customerProduct.urls.de !== null &&
                customerProduct.urls.de !== ""
            ) {
                return customerProduct.urls.de
            } else if (customer?.productUrl !== null && customer?.productUrl !== undefined && customer?.productUrl !== "") {
                return customer?.productUrl + customerProduct.productnr
            }
        }

        return ""
    }

    static isJSON(str) {
        try {
          JSON.parse(str);
          return true;
        } catch (error) {
          return false;
        }
      }

    static activDarkmode() {
        const cookies = new Cookies()
        let darkmodeactiv = false

        if (cookies.get("ppfrontend2_darkmode") == true) {
            document.body.classList.add("dark-mode")
            darkmodeactiv = true
        }

        return darkmodeactiv
    }

    static getLinkIcon() {
        return <i className="fas fa-link mx-auto" style={{ color: "gray" }} />
    }

    static rangeNumber(start, end) {
        return Array(end - start + 1)
            .fill()
            .map((_i, idx) => start + idx)
    }

    static checkStringInputProductNrs(input) {
        const regex = new RegExp(/[a-zA-Z0-9 #.,_-]/g)
        if (input.match(regex)) {
            return true
        }
        return false
    }

    static getDummyAvatarString(name, width, className) {
        const px = typeof width === "number" ? width : parseInt(width, 10)
        const fontSize = Number.isFinite(px) ? Math.max(12, Math.round(px * 0.6)) : 20
        return (
            <span
                className={
                    "d-inline-flex align-items-center justify-content-center rounded-circle " + (className || "")
                }
                style={{
                    width: typeof width === "number" ? width + "px" : width,
                    height: typeof width === "number" ? width + "px" : width,
                    backgroundColor: "#bdbdbd",
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: fontSize + "px",
                    fontWeight: 400,
                    lineHeight: 1,
                    flexShrink: 0
                }}
            >
                {name.substring(0, 1).toUpperCase()}
            </span>
        )
    }

    static setGenericTranslation(auth, i18n) {
        if (auth?.login?.customer?.genericTranslation?.length > 0) {
            auth?.login?.customer?.genericTranslation?.map((translation) => {
                if (translation?.name != "" && translation?.propertyName?.indexOf(":") > 0) {
                    const t_string = translation?.propertyName.split(":")
                    i18n.addResource(t_string[0], "translation", t_string[1], translation.name)
                }
            })
        }
    }

    static getCurrentAuthFromAuth(auth) {
        return auth?.login?.customerUsers?.length > 0 &&
            auth?.login?.token_original !== undefined &&
            auth?.login?.token !== auth?.login?.token_original
            ? auth.login.customerUsers.find((customerUser) => customerUser.token === auth.login.token)
            : auth.login
    }

    static isPositiveIntId(value) {
        return typeof value === "number" && Number.isInteger(value) && value > 0
    }

    static isValidCustomerId(value) {
        return PPUtils.isPositiveIntId(value)
    }

    static customerIdFromCustomerUser(customerUser) {
        if (customerUser == null) {
            return null
        }
        const fromCustomer = customerUser.customer?.id
        if (PPUtils.isValidCustomerId(fromCustomer)) {
            return fromCustomer
        }
        const fromUser = customerUser.user?.customerId
        if (PPUtils.isValidCustomerId(fromUser)) {
            return fromUser
        }
        return null
    }

    static getSelectedCustomerUser(auth = PPUtils.getLogin()) {
        if (auth == null || !Array.isArray(auth.customerUsers) || auth.customerUsers.length === 0) {
            return null
        }
        const hasOriginalToken = typeof auth.token_original === "string" && auth.token_original !== ""
        const hasCurrentToken = typeof auth.token === "string" && auth.token !== ""
        const isImpersonating = hasOriginalToken && hasCurrentToken && auth.token !== auth.token_original
        if (!isImpersonating) {
            return null
        }
        const selected = auth.customerUsers.find((customerUser) => customerUser.token === auth.token)
        if (selected == null) {
            return null
        }
        return selected
    }

    static getEffectiveCustomerId(auth = PPUtils.getLogin()) {
        if (auth == null) {
            return null
        }

        const selectedCustomerUser = PPUtils.getSelectedCustomerUser(auth)
        if (selectedCustomerUser != null) {
            return PPUtils.customerIdFromCustomerUser(selectedCustomerUser)
        }

        const loginCustomerId = auth.customer?.id
        if (PPUtils.isValidCustomerId(loginCustomerId)) {
            return loginCustomerId
        }

        const userCustomerId = auth.user?.customerId
        if (PPUtils.isValidCustomerId(userCustomerId)) {
            return userCustomerId
        }

        return null
    }

    static getCustomerRequestContext(auth = PPUtils.getLogin()) {
        if (auth == null) {
            return null
        }
        const customerId = PPUtils.getEffectiveCustomerId(auth)
        if (!PPUtils.isValidCustomerId(customerId)) {
            return null
        }
        const token = auth.token
        if (typeof token !== "string" || token === "") {
            return null
        }
        return { token, customerId }
    }

    static getEffectiveConfigShopsActivated(auth = PPUtils.getLogin()) {
        if (!auth) {
            return null
        }
        const selectedCustomerUser = PPUtils.getSelectedCustomerUser(auth)
        if (selectedCustomerUser?.user?.configShopsActivated) {
            return selectedCustomerUser.user.configShopsActivated
        }
        if (auth.customer?.user?.configShopsActivated && auth.token_original && auth.token !== auth.token_original) {
            return auth.customer.user.configShopsActivated
        }
        return auth.user?.configShopsActivated || null
    }

    static getCustomerName() {
        let currentAuth = this.getLogin()
        return currentAuth?.customer?.name;
    }

    static formatDateDE(value, withTime = false) {
        if (value == null || value === "") {
            return ""
        }
        const d = value instanceof Date ? value : new Date(value)
        if (Number.isNaN(d.getTime())) {
            return String(value)
        }
        const opts = withTime
            ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
            : { day: "2-digit", month: "2-digit", year: "numeric" }
        return d.toLocaleString("de-DE", opts)
    }

    static formatDateForBackend(date) {
        if (!date) {
            return null
        }
        const d = date instanceof Date ? date : new Date(date)
        if (Number.isNaN(d.getTime())) {
            return null
        }
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, "0")
        const day = String(d.getDate()).padStart(2, "0")
        return `${y}-${m}-${day}T12:00:00`
    }
}

export default PPUtils
